import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  X, Users, Lock, Smartphone, Mail, MessageSquare,
  ChevronDown, Key, AlertCircle
} from 'lucide-react';
import api from '../api';

const LOGO_URL = (process.env.PUBLIC_URL || '') + '/logo.png';

const validatePassword = (pw) => {
  if (!pw) return { valid: false, msg: '请输入密码' };
  if (pw.length < 8) return { valid: false, msg: '密码至少需要8个字符' };
  if (pw.length > 30) return { valid: false, msg: '密码不能超过30个字符' };
  if (!/[A-Z]/.test(pw)) return { valid: false, msg: '密码需包含至少1个大写字母（A-Z）' };
  if (!/[a-z]/.test(pw)) return { valid: false, msg: '密码需包含至少1个小写字母（a-z）' };
  if (!/[0-9]/.test(pw)) return { valid: false, msg: '密码需包含至少1个数字（0-9）' };
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw)) return { valid: false, msg: '密码需包含至少1个特殊字符（如 !@#$% 等）' };
  return { valid: true, msg: '' };
};

const LoginModal = ({ isOpen, onClose, onLogin }) => {
  const [mode, setMode] = useState('login');
  const [loginType, setLoginType] = useState('account');
  const [registerType, setRegisterType] = useState('username');
  const [forgotType, setForgotType] = useState('phone');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deactivatedAlert, setDeactivatedAlert] = useState(false);
  const errorTimeoutRef = useRef(null);
  const successTimeoutRef = useRef(null);
  const deactivatedTimeoutRef = useRef(null);

  useEffect(() => {
    if (error) {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(''), 5000);
    }
    return () => { if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current); };
  }, [error]);

  useEffect(() => {
    if (success) {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setSuccess(''), 3000);
    }
    return () => { if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current); };
  }, [success]);

  useEffect(() => {
    if (deactivatedAlert) {
      if (deactivatedTimeoutRef.current) clearTimeout(deactivatedTimeoutRef.current);
      deactivatedTimeoutRef.current = setTimeout(() => setDeactivatedAlert(false), 2000);
    }
    return () => { if (deactivatedTimeoutRef.current) clearTimeout(deactivatedTimeoutRef.current); };
  }, [deactivatedAlert]);

  const [countdown, setCountdown] = useState(0);

  const [loginAccount, setLoginAccount] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [phoneLoginPhone, setPhoneLoginPhone] = useState('');
  const [phoneLoginCode, setPhoneLoginCode] = useState('');
  const [phoneLoginInviteCode, setPhoneLoginInviteCode] = useState('');
  const [showInviteCode, setShowInviteCode] = useState(false);

  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');

  const [forgotPhone, setForgotPhone] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      resetForm();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  useEffect(() => {
    let timer;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const resetForm = () => {
    setMode('login');
    setLoginType('account');
    setRegisterType('username');
    setForgotType('phone');
    setIsLoading(false);
    setError('');
    setLoginAccount('');
    setLoginPassword('');
    setPhoneLoginPhone('');
    setPhoneLoginCode('');
    setPhoneLoginInviteCode('');
    setShowInviteCode(false);
    setUsername('');
    setPhone('');
    setEmail('');
    setVerifyCode('');
    setPassword('');
    setConfirmPassword('');
    setInviteCode('');
    setForgotPhone('');
    setForgotEmail('');
    setForgotCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    setCountdown(0);
  };

  const handleSendCode = (targetPhone) => {
    const phoneVal = targetPhone || phone;
    if (!phoneVal || !/^1[3-9]\d{9}$/.test(phoneVal)) {
      setError('请输入正确的手机号');
      return;
    }
    setCountdown(60);
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setDeactivatedAlert(false);
    setIsLoading(true);
    try {
      const result = await api.login(loginAccount, loginPassword);
      if (result.success) {
        setSuccess('登录成功');
        localStorage.setItem('user', JSON.stringify(result.data));
        if (onLogin) onLogin(result.data);
        setTimeout(() => { onClose(); }, 800);
      } else if (result.code === 403) {
        setDeactivatedAlert(true);
      } else if (result.code === 201) {
        setShowInviteCode(true);
        setError(result.msg || '首次登录需填写邀请码');
      } else {
        setError(result.msg || '登录失败');
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setError('');
    setDeactivatedAlert(false);
    if (!phoneLoginPhone || !/^1[3-9]\d{9}$/.test(phoneLoginPhone)) { setError('请输入正确的手机号'); return; }
    if (!phoneLoginCode || phoneLoginCode.length < 4) { setError('请输入验证码'); return; }
    setIsLoading(true);
    try {
      const result = await api.loginByPhone(phoneLoginPhone, phoneLoginCode, phoneLoginInviteCode);
      if (result.success) {
        setSuccess('登录成功');
        localStorage.setItem('user', JSON.stringify(result.data));
        if (onLogin) onLogin(result.data);
        setTimeout(() => { onClose(); }, 800);
      } else if (result.code === 403) {
        setDeactivatedAlert(true);
      } else if (result.code === 201) {
        setShowInviteCode(true);
        setError(result.msg || '首次登录需填写邀请码');
      } else {
        setError(result.msg || '登录失败');
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      let result;
      const pwCheck = validatePassword(password);
      if (!pwCheck.valid) { setError(pwCheck.msg); setIsLoading(false); return; }
      if (registerType === 'username') {
        if (!username || !password || !inviteCode) { setError('请填写完整信息'); setIsLoading(false); return; }
        result = await api.registerUsername(username, password, inviteCode);
      } else if (registerType === 'phone') {
        if (!phone || !verifyCode || !password || !inviteCode) { setError('请填写完整信息'); setIsLoading(false); return; }
        result = await api.registerPhone(phone, verifyCode, password, inviteCode);
      } else {
        if (!email || !password || !confirmPassword || !inviteCode) { setError('请填写完整信息'); setIsLoading(false); return; }
        if (password !== confirmPassword) { setError('两次输入的密码不一致'); setIsLoading(false); return; }
        result = await api.registerEmail(email, password, confirmPassword, inviteCode);
      }

      if (result.success) {
        setSuccess('注册成功   请登录');
        setTimeout(() => { setMode('login'); setRegisterType('username'); setUsername(''); setPhone(''); setEmail(''); setVerifyCode(''); setPassword(''); setConfirmPassword(''); setInviteCode(''); }, 1000);
      } else {
        setError(result.msg || '注册失败');
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const targetPhone = forgotType === 'phone' ? forgotPhone : '';
      const targetEmail = forgotType === 'email' ? forgotEmail : '';

      if (!forgotCode) { setError('请输入验证码'); setIsLoading(false); return; }
      const newPwCheck = validatePassword(newPassword);
      if (!newPwCheck.valid) { setError(newPwCheck.msg); setIsLoading(false); return; }
      if (newPassword !== confirmNewPassword) { setError('两次输入的新密码不一致'); setIsLoading(false); return; }
      if (forgotType === 'phone' && (!targetPhone || !/^1[3-9]\d{9}$/.test(targetPhone))) { setError('请输入正确的手机号'); setIsLoading(false); return; }
      if (forgotType === 'email' && (!targetEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(targetEmail))) { setError('请输入正确的邮箱'); setIsLoading(false); return; }

      const resetData = {
        verify_code: forgotCode,
        new_password: newPassword,
        phone: targetPhone,
        email: targetEmail,
      };

      const updateResult = await api.profile.resetPasswordByVerify(resetData);
      if (updateResult.success) {
        setSuccess('密码重置成功，请使用新密码登录');
        setTimeout(() => { setMode('login'); setForgotPhone(''); setForgotEmail(''); setForgotCode(''); setNewPassword(''); setConfirmNewPassword(''); }, 1500);
      } else {
        setError(updateResult.msg || '密码重置失败');
      }
    } catch (err) {
      setError('操作失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass-card border border-white/20 rounded-3xl p-8 w-full max-w-md shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/10 transition-colors z-10">
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-6 shrink-0">
          <img src={LOGO_URL} alt="AI CREATOR" className="w-20 h-20 mx-auto rounded-2xl object-contain mb-4 shadow-lg bg-gradient-to-br from-luxury-gold/20 to-amber-300/20 p-2" />
          <h2 className="text-3xl font-bold gradient-text mb-2">
            {mode === 'login' ? '欢迎来到 AI CREATOR' : mode === 'register' ? '创建账号' : '找回密码'}
          </h2>
          <p className="text-apple-gray text-sm">
            {mode === 'login' ? '登录以继续使用' : mode === 'register' ? '加入我们，开启AI创作之旅' : '通过验证码重置您的密码'}
          </p>
        </div>

        {deactivatedAlert && (
          <div className="mb-4 p-4 bg-red-500/20 border-2 border-red-500/50 rounded-xl flex items-center gap-3 shrink-0 animate-pulse">
            <AlertCircle className="w-6 h-6 text-red-400 shrink-0" />
            <div>
              <p className="text-red-400 font-bold text-base">⚠️ 该账户已注销</p>
              <p className="text-red-300/70 text-xs mt-1">此账户已被注销，无法登录。如需帮助请联系管理员</p>
            </div>
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm shrink-0">{error}</div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-xl text-green-400 text-sm shrink-0">{success}</div>
        )}

        <div className="overflow-y-auto scrollbar-hide flex-1 -mr-2 pr-2">
          {mode === 'login' ? (
            <div className="space-y-5">
              <div className="flex gap-6 border-b border-white/10 pb-3">
                {['account', 'phone'].map((type) => (
                  <button key={type} type="button" onClick={() => setLoginType(type)} className={`text-base font-medium pb-1 transition-colors relative ${loginType === type ? 'text-blue-400' : 'text-apple-gray hover:text-white'}`}>
                    {type === 'account' ? '账号登录' : '手机号登录'}
                    {loginType === type && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 rounded-full" />}
                  </button>
                ))}
              </div>
              {loginType === 'account' ? (
                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className="text-sm text-apple-gray mb-2 block">账号</label>
                    <div className="relative">
                      <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                      <input type="text" value={loginAccount} onChange={(e) => setLoginAccount(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="用户名/手机号/邮箱" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-apple-gray mb-2 block">密码</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                      <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="••••••••" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={() => setMode('register')} className="text-sm text-luxury-gold hover:text-amber-300 transition-colors">立即注册</button>
                    <button type="button" onClick={() => { setMode('forgot'); setForgotType('phone'); }} className="text-sm text-luxury-gold hover:text-amber-300 transition-colors">忘记密码？</button>
                  </div>
                  <button type="submit" disabled={isLoading} className="w-full py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-luxury-gold via-amber-300 to-luxury-gold bg-[length:200%_100%] hover:bg-[length:100%_100%] transition-all duration-700 shadow-lg shadow-luxury-gold/20 hover:shadow-luxury-gold/40 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isLoading ? '登录中...' : '登录'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handlePhoneLogin} className="space-y-5">
                  <div>
                    <div className="relative">
                      <select className="absolute left-0 top-1/2 -translate-y-1/2 bg-transparent border-r border-white/20 text-apple-gray text-sm pl-4 pr-8 py-4 outline-none appearance-none cursor-pointer">
                        <option value="+86" className="bg-zinc-900">+86</option>
                      </select>
                      <ChevronDown className="absolute left-14 top-1/2 -translate-y-1/2 w-3 h-3 text-apple-gray pointer-events-none" />
                      <input type="tel" maxLength={11} value={phoneLoginPhone} onChange={(e) => setPhoneLoginPhone(e.target.value.replace(/\D/g, ''))} className="w-full bg-white/5 border border-white/15 rounded-xl pl-24 pr-4 py-4 focus:border-blue-400 focus:outline-none transition-colors" placeholder="请输入手机号" />
                    </div>
                  </div>
                  <div>
                    <div className="flex gap-3">
                      <div className="relative flex-1">
                        <input type="text" maxLength={6} value={phoneLoginCode} onChange={(e) => setPhoneLoginCode(e.target.value.replace(/\D/g, ''))} className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-4 focus:border-blue-400 focus:outline-none transition-colors" placeholder="请输入验证码" />
                      </div>
                      <button type="button" disabled={countdown > 0 || !/^1[3-9]\d{9}$/.test(phoneLoginPhone)} onClick={() => { if (/^1[3-9]\d{9}$/.test(phoneLoginPhone)) { setCountdown(60); setError(''); } }} className={`px-5 py-4 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${countdown > 0 || !/^1[3-9]\d{9}$/.test(phoneLoginPhone) ? 'bg-white/10 text-apple-gray cursor-not-allowed' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`}>
                        {countdown > 0 ? `${countdown}s` : '获取验证码'}
                      </button>
                    </div>
                  </div>
                  {showInviteCode && (
                    <div>
                      <input type="text" value={phoneLoginInviteCode} onChange={(e) => setPhoneLoginInviteCode(e.target.value)} className="w-full bg-white/5 border border-blue-400/50 rounded-xl px-4 py-4 focus:border-blue-400 focus:outline-none transition-colors" placeholder="请输入邀请码（首次登录必填）" />
                    </div>
                  )}
                  <button type="submit" disabled={isLoading || !phoneLoginPhone || !phoneLoginCode || phoneLoginCode.length < 4 || (showInviteCode && !phoneLoginInviteCode)} className="w-full py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500 bg-[length:200%_100%] hover:bg-[length:100%_100%] transition-all duration-700 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isLoading ? '登录中...' : '登录'}
                  </button>
                </form>
              )}
              <p className="text-xs text-apple-gray/60 text-center pt-1 leading-relaxed">登录即视为您已阅读并同意<Link to="/terms" className="text-blue-400 hover:text-blue-300 transition-colors mx-0.5" onClick={(e) => { e.stopPropagation(); onClose(); }}>服务条款</Link>和<Link to="/privacy" className="text-blue-400 hover:text-blue-300 transition-colors mx-0.5" onClick={(e) => { e.stopPropagation(); onClose(); }}>隐私政策</Link></p>
            </div>
          ) : mode === 'forgot' ? (
            <div className="space-y-5">
              <div className="flex gap-2 mb-4">
                {['phone', 'email'].map((type) => (
                  <button key={type} type="button" onClick={() => setForgotType(type)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${forgotType === type ? 'bg-luxury-gold text-white' : 'bg-white/5 text-apple-gray hover:bg-white/10'}`}>
                    {type === 'phone' ? '手机号找回' : '邮箱找回'}
                  </button>
                ))}
              </div>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                {forgotType === 'phone' && (
                  <>
                    <div>
                      <label className="text-sm text-apple-gray mb-2 block">手机号</label>
                      <div className="relative">
                        <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                        <input type="text" maxLength={11} value={forgotPhone} onChange={(e) => setForgotPhone(e.target.value.replace(/\D/g, ''))} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="请输入绑定手机号" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-apple-gray mb-2 block">验证码</label>
                      <div className="flex gap-3">
                        <div className="relative flex-1">
                          <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                          <input type="text" maxLength={6} value={forgotCode} onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ''))} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="6位验证码" />
                        </div>
                        <button type="button" onClick={() => handleSendCode(forgotPhone)} disabled={countdown > 0} className="px-4 py-4 rounded-xl bg-luxury-gold/20 text-luxury-gold hover:bg-luxury-gold/30 transition-colors disabled:opacity-50 whitespace-nowrap text-sm">
                          {countdown > 0 ? `${countdown}s` : '获取验证码'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {forgotType === 'email' && (
                  <>
                    <div>
                      <label className="text-sm text-apple-gray mb-2 block">邮箱</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                        <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="your@email.com" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-apple-gray mb-2 block">验证码</label>
                      <div className="flex gap-3">
                        <div className="relative flex-1">
                          <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                          <input type="text" maxLength={6} value={forgotCode} onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ''))} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="6位验证码" />
                        </div>
                        <button type="button" onClick={() => { setCountdown(60); setError(''); }} disabled={countdown > 0} className="px-4 py-4 rounded-xl bg-luxury-gold/20 text-luxury-gold hover:bg-luxury-gold/30 transition-colors disabled:opacity-50 whitespace-nowrap text-sm">
                          {countdown > 0 ? `${countdown}s` : '获取验证码'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
                <div>
                  <label className="text-sm text-apple-gray mb-2 block">新密码</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="8-30位，含大小写+数字+特殊字符" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-apple-gray mb-2 block">确认新密码</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                    <input type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="再次输入新密码" />
                  </div>
                </div>
                <button type="submit" disabled={isLoading} className="w-full py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-luxury-gold via-amber-300 to-luxury-gold bg-[length:200%_100%] hover:bg-[length:100%_100%] transition-all duration-700 shadow-lg shadow-luxury-gold/20 hover:shadow-luxury-gold/40 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isLoading ? '处理中...' : '重置密码'}
                </button>
              </form>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex gap-2 mb-6">
                {['username', 'phone', 'email'].map((type) => (
                  <button key={type} type="button" onClick={() => setRegisterType(type)} className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${registerType === type ? 'bg-luxury-gold text-white' : 'bg-white/5 text-apple-gray hover:bg-white/10'}`}>
                    {type === 'username' ? '用户名' : type === 'phone' ? '手机号' : '邮箱'}
                  </button>
                ))}
              </div>

              <form onSubmit={handleRegister} className="space-y-5">
                {registerType === 'username' && (
                  <div>
                    <label className="text-sm text-apple-gray mb-2 block">用户名</label>
                    <div className="relative">
                      <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                      <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="3-20个字符" />
                    </div>
                  </div>
                )}

                {registerType === 'phone' && (
                  <>
                    <div>
                      <label className="text-sm text-apple-gray mb-2 block">手机号</label>
                      <div className="relative">
                        <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                        <input type="text" maxLength={11} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="请输入11位手机号" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-apple-gray mb-2 block">验证码</label>
                      <div className="flex gap-3">
                        <div className="relative flex-1">
                          <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                          <input type="text" maxLength={6} value={verifyCode} onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="请输入6位验证码" />
                        </div>
                        <button type="button" onClick={handleSendCode} disabled={countdown > 0} className="px-4 py-4 rounded-xl bg-luxury-gold/20 text-luxury-gold hover:bg-luxury-gold/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
                          {countdown > 0 ? `${countdown}s` : '获取验证码'}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {registerType === 'email' && (
                  <div>
                    <label className="text-sm text-apple-gray mb-2 block">邮箱</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="your@email.com" />
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm text-apple-gray mb-2 block">密码</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="8-30位，含大小写+数字+特殊字符" />
                  </div>
                </div>

                {registerType === 'email' && (
                  <div>
                    <label className="text-sm text-apple-gray mb-2 block">确认密码</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                      <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="再次输入密码" />
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm text-apple-gray mb-2 block">邀请码</label>
                  <div className="relative">
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                    <input type="text" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-4 focus:border-luxury-gold focus:outline-none transition-colors" placeholder="请输入邀请码" />
                  </div>
                </div>

                <button type="submit" disabled={isLoading} className="w-full py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-luxury-gold via-amber-300 to-luxury-gold bg-[length:200%_100%] hover:bg-[length:100%_100%] transition-all duration-700 shadow-lg shadow-luxury-gold/20 hover:shadow-luxury-gold/40 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isLoading ? '注册中...' : '立即注册'}
                </button>
              </form>
            </div>
          )}
        </div>

        <div className="mt-6 space-y-4 shrink-0 pt-4 border-t border-white/10">
          <div className="text-center text-sm text-apple-gray">
            {mode === 'login' ? null : mode === 'forgot' ? (
              <>想起密码了？<button type="button" onClick={() => setMode('login')} className="text-luxury-gold hover:text-amber-300 transition-colors ml-1">返回登录</button></>
            ) : (
              <>已有账号？<button type="button" onClick={() => setMode('login')} className="text-luxury-gold hover:text-amber-300 transition-colors ml-1">立即登录</button></>
            )}
          </div>
          <button type="button" onClick={onClose} className="w-full py-3 rounded-xl font-semibold text-apple-gray border border-white/15 hover:border-white/25 hover:bg-white/8 transition-all duration-400 flex items-center justify-center gap-2">
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export { LoginModal };
